import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseDataService } from './base-data.service';

/**
 * Response body returned by every feature's Purge endpoint. The API can return HTTP 200 while
 * `isSuccess` is false (e.g. some records couldn't be purged due to a business rule) - callers
 * must inspect `isSuccess`/`message` rather than assuming the call succeeding means the purge did.
 */
export interface PurgeResultVm {
  requestedCount: number;
  purgedCount: number;
  skippedCount: number;
  isSuccess: boolean;
  message: string;
}

/**
 * Cross-cutting "Purge" and "Export to Excel" calls shared by every main list feature service.
 *
 * Both actions are POSTed with the exact same filter payload each feature already sends to its
 * own list API, so the backend can apply Purge/Export against the currently filtered result set
 * rather than the whole table.
 */
@Injectable({
  providedIn: 'root',
})
export class ListActionsService {
  constructor(private baseDataService: BaseDataService) {}

  /**
   * Permanently removes every record matching the given list filters.
   * @param apiBasePath the feature's own base API path (e.g. `api/Orders`)
   * @param filters the same filter object already passed to that feature's list API call
   */
  purgeList<TFilter>(apiBasePath: string, filters: TFilter): Observable<PurgeResultVm> {
    return this.baseDataService.makePostCall(`${apiBasePath}/list/purge`, filters);
  }

  /**
   * Requests a server-generated Excel export of every record matching the given list filters.
   * @param apiBasePath the feature's own base API path (e.g. `api/Orders`)
   * @param filters the same filter object already passed to that feature's list API call
   */
  exportListToExcel<TFilter>(apiBasePath: string, filters: TFilter): Observable<Blob> {
    return this.baseDataService.makePostDownloadCall(`${apiBasePath}/list/export`, filters);
  }
}
